import QuestionPage from '@app-studio-web/ui/pages/QuestionPage'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useSubmit } from 'react-router-dom'
import useUpdateMode from '../../hooks/useUpdateMode'
import { getAllAnswers, saveAnswer } from '../../utils/onboardingStorage'

export default function BedtimeRoutineManagement() {
  const submit = useSubmit()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const value = searchParams.get('value')
  const preselectedValue = value ? value : undefined
  const { isUpdate, postToReactNative } = useUpdateMode()

  // Get the selected bedtime routine from previous answers
  const allAnswers = getAllAnswers()

  const bedTimeRoutineType = allAnswers.bedtimeRoutine?.answer_key

  // Get question config based on bedtime routine type
  const getQuestionConfig = () => {
    switch (bedTimeRoutineType) {
      case 'Calm & quiet':
        return {
          questionKey: 'calmQuietRoutine',
          heading:
            'pages.onboarding.bedtimeRoutineManagement.calmQuietRoutine.heading',
          question: t(
            'pages.onboarding.bedtimeRoutineManagement.calmQuietRoutine.heading',
            { lng: 'en' },
          ),
          options: [
            {
              value: 'Reading',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.calmQuietRoutine.options.reading',
              emoji: '📖',
            },
            {
              value: 'Journaling',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.calmQuietRoutine.options.journaling',
              emoji: '✍️',
            },
            {
              value: 'Breathing / Meditation',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.calmQuietRoutine.options.meditation',
              emoji: '🧘',
            },
            {
              value: 'Not sure yet',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.calmQuietRoutine.options.notSure',
              emoji: '🤔',
            },
          ],
        }

      case 'Light movement':
        return {
          questionKey: 'lightMovementRoutine',
          heading:
            'pages.onboarding.bedtimeRoutineManagement.lightMovementRoutine.heading',
          question: t(
            'pages.onboarding.bedtimeRoutineManagement.lightMovementRoutine.heading',
            { lng: 'en' },
          ),
          options: [
            {
              value: 'Stretching',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.lightMovementRoutine.options.stretching',
              emoji: '🤸',
            },
            {
              value: 'Yoga',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.lightMovementRoutine.options.yoga',
              emoji: '🧘',
            },
            {
              value: 'Short walk',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.lightMovementRoutine.options.walk',
              emoji: '🚶',
            },
            {
              value: 'Not sure yet',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.lightMovementRoutine.options.notSure',
              emoji: '🤔',
            },
          ],
        }

      case 'Tech-based':
        return {
          questionKey: 'techBasedRoutine',
          heading:
            'pages.onboarding.bedtimeRoutineManagement.techBasedRoutine.heading',
          question: t(
            'pages.onboarding.bedtimeRoutineManagement.techBasedRoutine.heading',
            { lng: 'en' },
          ),
          options: [
            {
              value: 'Sleep sounds',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.techBasedRoutine.options.sleepSounds',
              emoji: '🎶',
            },
            {
              value: 'Guided meditation',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.techBasedRoutine.options.guidedMeditation',
              emoji: '📲',
            },
            {
              value: 'Sleep story',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.techBasedRoutine.options.sleepStory',
              emoji: '📺',
            },
            {
              value: 'Not sure yet',
              translationKey:
                'pages.onboarding.bedtimeRoutineManagement.techBasedRoutine.options.notSure',
              emoji: '🤔',
            },
          ],
        }

      case 'No bedtime routine':
        return {
          questionKey: 'noBedtimeRoutineHabit',
          heading: 'pages.onboarding.habits.noBedtimeRoutineHabit.heading',
          question: t('pages.onboarding.habits.noBedtimeRoutineHabit.heading', {
            lng: 'en',
          }),
          options: [
            {
              value: 'Watch TV/Netflix',
              translationKey:
                'pages.onboarding.habits.noBedtimeRoutineHabit.options.watchingTV',
              emoji: '📺',
            },
            {
              value: 'Work/study late',
              translationKey:
                'pages.onboarding.habits.noBedtimeRoutineHabit.options.workingLate',
              emoji: '💻',
            },
            {
              value: 'Nothing specific',
              translationKey:
                'pages.onboarding.habits.noBedtimeRoutineHabit.options.nothing',
              emoji: '❓',
            },
          ],
        }

      default:
        return null
    }
  }

  const config = getQuestionConfig()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!config) return

    const formData = new FormData(event.currentTarget)
    const selectedValue = formData.get('answer') as string

    if (!selectedValue) return

    const answer = {
      question_key: config.questionKey,
      question: config.question,
      answer_key: selectedValue,
      answer: selectedValue,
    }

    if (isUpdate) {
      postToReactNative(answer)
    } else {
      saveAnswer(config.questionKey, answer)
      submit(event.currentTarget)
    }
  }

  // If no bedtime routine is selected, show error message
  if (!config) {
    return null
  }

  // Transform options to the format expected by QuestionPage
  const buttons = config.options.map((option) => ({
    text: `${option.emoji} ${t(option.translationKey)}`,
    value: option.value,
  }))

  return (
    <QuestionPage
      heading={t(config.heading)}
      buttons={buttons}
      onSubmit={handleSubmit}
      headingSize="text-3xl text-center whitespace-pre-line"
      buttonTextSize="text-xl"
      buttonBgColor="bg-white"
      preselectedValue={preselectedValue}
    />
  )
}
